/**
 * Daily RDS storage snapshot (#2375 / #2354, lane B10).
 *
 * Gateway and platform each expose `GET /internal/storage/relation-sizes`
 * (read-only pg_catalog). control-loops fans the two gateway regions plus
 * the Tokyo platform DB into `POST /internal/cron/storage-snapshot`, which
 * upserts one platform-owned row per (day, source, region, relation).
 *
 * Pure observability. The SQL that produces these rows is duplicated in
 * the two route modules (separate PgDatabase bindings); this module stays
 * type-only plus the row-coercion helper.
 */

export const STORAGE_SNAPSHOT_RETENTION_DAYS = 400;

export const STORAGE_SNAPSHOT_SOURCES = ['gateway', 'platform'] as const;
export type StorageSnapshotSource = (typeof STORAGE_SNAPSHOT_SOURCES)[number];

export const STORAGE_SNAPSHOT_RELKINDS = ['table', 'index'] as const;
export type StorageSnapshotRelkind = (typeof STORAGE_SNAPSHOT_RELKINDS)[number];

export interface StorageRelationSample {
  readonly schema_name: string;
  readonly relation_name: string;
  /** Empty string on the table row itself. */
  readonly index_name: string;
  readonly relkind: StorageSnapshotRelkind;
  readonly total_bytes: number;
  readonly heap_bytes: number | null;
  readonly index_bytes: number | null;
  readonly toast_bytes: number | null;
  readonly n_live_tup: number | null;
  readonly n_dead_tup: number | null;
  readonly idx_scan: number | null;
  readonly last_autovacuum: string | null;
  readonly last_autoanalyze: string | null;
}

export interface StorageSizesResponse {
  readonly source: StorageSnapshotSource;
  readonly region: string;
  readonly database_bytes: number;
  /** ISO-8601 instant the catalog was read. */
  readonly sampled_at: string;
  readonly relations: readonly StorageRelationSample[];
}

export interface StorageSnapshotCronRequest {
  readonly samples: readonly StorageSizesResponse[];
}

export interface StorageSnapshotCronResponse {
  readonly upserted_rows: number;
  readonly pruned_rows: number;
  readonly samples: number;
  readonly errors: readonly string[];
}

const RELKIND_SET: ReadonlySet<string> = new Set(STORAGE_SNAPSHOT_RELKINDS);

function readNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function readTimestamp(value: unknown): string | null {
  if (value instanceof Date) {
    const iso = value.toISOString();
    return Number.isFinite(value.getTime()) ? iso : null;
  }
  if (typeof value === 'string' && value.length > 0) return value;
  return null;
}

function readNonEmptyName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (value.length === 0) return null;
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Coerce a pg_catalog row (bigint-as-string or JS number) into a wire
 * sample. Rows that cannot be coerced are dropped rather than failing
 * the whole snapshot: one unreadable catalog entry must not wipe the day.
 */
export function storageRelationSamplesFromRows(
  rows: readonly unknown[],
): readonly StorageRelationSample[] {
  const samples: StorageRelationSample[] = [];
  for (const entry of rows) {
    if (!isRecord(entry)) continue;
    const schema_name = readNonEmptyName(entry.schema_name);
    const relation_name = readNonEmptyName(entry.relation_name);
    if (schema_name === null || relation_name === null) continue;
    const relkindRaw = entry.relkind;
    if (typeof relkindRaw !== 'string' || !RELKIND_SET.has(relkindRaw)) continue;
    const relkind: StorageSnapshotRelkind = relkindRaw === 'index' ? 'index' : 'table';
    const total_bytes = readNullableNumber(entry.total_bytes);
    if (total_bytes === null || total_bytes < 0) continue;
    const index_name = relkind === 'table'
      ? ''
      : (typeof entry.index_name === 'string' ? entry.index_name : '');
    if (relkind === 'index' && index_name.length === 0) continue;
    samples.push({
      schema_name,
      relation_name,
      index_name,
      relkind,
      total_bytes,
      heap_bytes: readNullableNumber(entry.heap_bytes),
      index_bytes: readNullableNumber(entry.index_bytes),
      toast_bytes: readNullableNumber(entry.toast_bytes),
      n_live_tup: readNullableNumber(entry.n_live_tup),
      n_dead_tup: readNullableNumber(entry.n_dead_tup),
      idx_scan: readNullableNumber(entry.idx_scan),
      last_autovacuum: readTimestamp(entry.last_autovacuum),
      last_autoanalyze: readTimestamp(entry.last_autoanalyze),
    });
  }
  return samples;
}

export function readStorageDatabaseBytes(value: unknown): number | null {
  const parsed = readNullableNumber(value);
  if (parsed === null || parsed < 0) return null;
  return parsed;
}
